// NEW_FILE_JUSTIFIED: Review step for bulk import — categorized policy review with enrichment diffs
import { useMemo, useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { PlusCircle, RefreshCw, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EnrichmentDiffView } from './EnrichmentDiffView';
import type {
  IBulkImportReviewStepProps,
  IBulkImportPolicyMatch,
  TBulkPolicyAction,
} from '@/@types/bulkImport';

interface IGroupState {
  // LOCAL_TYPE_JUSTIFIED: Internal group expansion state — tightly coupled to this component only
  newExpanded: boolean;
  enrichmentExpanded: boolean;
  duplicateExpanded: boolean;
}

export function BulkImportReviewStep({
  matchResults,
  orgTypes,
  onActionChange,
  onBatchActionChange,
  onTypeChange,
  onExecute,
  onCancel,
  isProcessing,
  hasUnassignedTypes,
}: IBulkImportReviewStepProps) {
  const [groupState, setGroupState] = useState<IGroupState>({
    newExpanded: true,
    enrichmentExpanded: true,
    duplicateExpanded: false,
  });

  const groups = useMemo(() => {
    const newPolicies: Array<{ match: IBulkImportPolicyMatch; index: number }> = [];
    const enrichment: Array<{ match: IBulkImportPolicyMatch; index: number }> = [];
    const duplicates: Array<{ match: IBulkImportPolicyMatch; index: number }> = [];

    matchResults.forEach((match, index) => {
      if (match.category === 'new') newPolicies.push({ match, index });
      else if (match.category === 'enrichment') enrichment.push({ match, index });
      else duplicates.push({ match, index });
    });

    return { newPolicies, enrichment, duplicates };
  }, [matchResults]);

  const counts = useMemo(
    () => ({
      total: matchResults.length,
      toCreate: matchResults.filter(m => m.action === 'create').length,
      toEnrich: matchResults.filter(m => m.action === 'enrich').length,
      toSkip: matchResults.filter(m => m.action === 'skip').length,
    }),
    [matchResults]
  );

  const toggleGroup = useCallback((group: keyof IGroupState) => {
    setGroupState(prev => ({ ...prev, [group]: !prev[group] }));
  }, []);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold text-foreground">Review Extracted Policies</h2>
        <p className="text-sm text-muted-foreground">
          {counts.total} policies extracted. Review and approve before processing.
        </p>
        <div className="flex items-center gap-3 mt-1">
          <Badge variant="secondary" className="gap-1.5">
            <PlusCircle className="w-3 h-3" aria-hidden="true" />
            {counts.toCreate} to create
          </Badge>
          <Badge variant="secondary" className="gap-1.5">
            <RefreshCw className="w-3 h-3" aria-hidden="true" />
            {counts.toEnrich} to enrich
          </Badge>
          <Badge variant="outline" className="gap-1.5">
            <Copy className="w-3 h-3" aria-hidden="true" />
            {counts.toSkip} to skip
          </Badge>
        </div>
      </div>

      {/* Action bar — top */}
      <ActionBar
        onCancel={onCancel}
        onExecute={onExecute}
        isProcessing={isProcessing}
        hasUnassignedTypes={hasUnassignedTypes}
        counts={counts}
      />

      {groups.newPolicies.length > 0 && (
        <PolicyGroup
          title="New Policies"
          description="No match — will be created"
          count={groups.newPolicies.length}
          expanded={groupState.newExpanded}
          onToggle={() => toggleGroup('newExpanded')}
          variant="new"
        >
          <label className="flex items-center gap-2 mb-2 cursor-pointer">
            <Checkbox
              checked={
                groups.newPolicies.every(({ match }) => match.action !== 'skip')
                  ? true
                  : groups.newPolicies.every(({ match }) => match.action === 'skip')
                    ? false
                    : 'indeterminate'
              }
              onCheckedChange={checked => {
                onBatchActionChange(
                  groups.newPolicies.map(({ index }) => ({
                    index,
                    action: checked ? 'create' : 'skip',
                  }))
                );
              }}
            />
            <span className="text-sm text-muted-foreground">
              {groups.newPolicies.every(({ match }) => match.action !== 'skip')
                ? 'Deselect all'
                : 'Select all'}
            </span>
          </label>
          {groups.newPolicies.map(({ match, index }) => (
            <PolicyRow
              key={index}
              match={match}
              index={index}
              onActionChange={onActionChange}
              orgTypes={orgTypes}
              onTypeChange={onTypeChange}
            />
          ))}
        </PolicyGroup>
      )}

      {groups.enrichment.length > 0 && (
        <PolicyGroup
          title="Enrichment"
          description="Policy exists — new data available"
          count={groups.enrichment.length}
          expanded={groupState.enrichmentExpanded}
          onToggle={() => toggleGroup('enrichmentExpanded')}
          variant="enrichment"
        >
          <label className="flex items-center gap-2 mb-2 cursor-pointer">
            <Checkbox
              checked={
                groups.enrichment.every(({ match }) => match.action !== 'skip')
                  ? true
                  : groups.enrichment.every(({ match }) => match.action === 'skip')
                    ? false
                    : 'indeterminate'
              }
              onCheckedChange={checked => {
                onBatchActionChange(
                  groups.enrichment.map(({ index }) => ({
                    index,
                    action: checked ? 'enrich' : 'skip',
                  }))
                );
              }}
            />
            <span className="text-sm text-muted-foreground">
              {groups.enrichment.every(({ match }) => match.action !== 'skip')
                ? 'Deselect all'
                : 'Select all'}
            </span>
          </label>
          {groups.enrichment.map(({ match, index }) => (
            <PolicyRow
              key={index}
              match={match}
              index={index}
              onActionChange={onActionChange}
              orgTypes={orgTypes}
              onTypeChange={onTypeChange}
              showDiff
            />
          ))}
        </PolicyGroup>
      )}

      {groups.duplicates.length > 0 && (
        <PolicyGroup
          title="Exact Duplicates"
          description="No new data — no action needed"
          count={groups.duplicates.length}
          expanded={groupState.duplicateExpanded}
          onToggle={() => toggleGroup('duplicateExpanded')}
          variant="duplicate"
        >
          {groups.duplicates.map(({ match, index }) => (
            <PolicyRow
              key={index}
              match={match}
              index={index}
              onActionChange={onActionChange}
              orgTypes={orgTypes}
              onTypeChange={onTypeChange}
            />
          ))}
        </PolicyGroup>
      )}
    </div>
  );
}

interface IActionBarProps {
  // LOCAL_TYPE_JUSTIFIED: Props for ActionBar sub-component — only used within this file
  onCancel: () => void;
  onExecute: () => void;
  isProcessing: boolean;
  hasUnassignedTypes: boolean;
  counts: { toCreate: number; toEnrich: number };
}

function ActionBar({
  onCancel,
  onExecute,
  isProcessing,
  hasUnassignedTypes,
  counts,
}: IActionBarProps) {
  return (
    <div className="sticky top-0 z-10 flex justify-end items-center gap-2 py-3 border-y border-border bg-background/95 backdrop-blur-sm">
      <Button variant="outline" onClick={onCancel} disabled={isProcessing}>
        Cancel
      </Button>
      <Button
        onClick={onExecute}
        disabled={
          isProcessing || hasUnassignedTypes || (counts.toCreate === 0 && counts.toEnrich === 0)
        }
      >
        {isProcessing ? 'Processing...' : `Create ${counts.toCreate} & Enrich ${counts.toEnrich}`}
      </Button>
    </div>
  );
}

interface IPolicyGroupProps {
  // LOCAL_TYPE_JUSTIFIED: Props for PolicyGroup sub-component defined in same file — not reused elsewhere
  title: string;
  description: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  variant: 'new' | 'enrichment' | 'duplicate';
  children: React.ReactNode;
}

function PolicyGroup({
  title,
  description,
  count,
  expanded,
  onToggle,
  variant,
  children,
}: IPolicyGroupProps) {
  const borderColor = {
    new: 'border-emerald-500/30',
    enrichment: 'border-amber-500/30',
    duplicate: 'border-muted',
  }[variant];

  return (
    <div className={cn('rounded-lg border', borderColor)}>
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          )}
          <span className="text-sm font-medium text-foreground">{title}</span>
          <Badge variant="secondary" className="text-xs">
            {count}
          </Badge>
        </div>
        <span className="text-xs text-muted-foreground">{description}</span>
      </button>
      {expanded && <div className="px-4 pb-4 space-y-2">{children}</div>}
    </div>
  );
}

interface IPolicyRowProps {
  // LOCAL_TYPE_JUSTIFIED: Props for PolicyRow sub-component defined in same file — not reused elsewhere
  match: IBulkImportPolicyMatch;
  index: number;
  onActionChange: (index: number, action: TBulkPolicyAction) => void;
  orgTypes: Array<{ _id: string; name: string }>;
  onTypeChange: (index: number, typeId: string) => void;
  showDiff?: boolean;
}

function PolicyRow({
  match,
  index,
  onActionChange,
  orgTypes,
  onTypeChange,
  showDiff,
}: IPolicyRowProps) {
  const { extracted } = match;
  const [diffExpanded, setDiffExpanded] = useState(false);
  const assignedType = orgTypes.find(t => t._id === match.assignedTypeId);
  const showTypeWarning = match.action === 'create' && !match.assignedTypeId;

  return (
    <div
      className={cn(
        'rounded-md border bg-card p-3 space-y-2',
        showTypeWarning ? 'border-destructive/50' : 'border-border'
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Checkbox
            checked={match.action !== 'skip'}
            onCheckedChange={checked =>
              onActionChange(
                index,
                checked ? (match.category === 'enrichment' ? 'enrich' : 'create') : 'skip'
              )
            }
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-foreground truncate">
                {extracted.policyNumber ?? 'No number'}
              </span>
              {extracted.policyType && (
                <Badge variant="outline" className="text-xs shrink-0">
                  {extracted.policyType}
                </Badge>
              )}
              {match.existingRenewalCount !== undefined && match.existingRenewalCount > 0 && (
                <Badge variant="secondary" className="text-xs shrink-0">
                  Renewal #{match.existingRenewalCount + 1}
                </Badge>
              )}
              {match.isOlderExtraction && (
                <Badge variant="destructive" className="text-xs shrink-0">
                  Older version
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">
              {[
                extracted.insuredName,
                extracted.regNo,
                extracted.expiryDate ? `Expires: ${extracted.expiryDate}` : null,
                extracted.derivedPremium ? `\u20ac${extracted.derivedPremium}` : null,
              ]
                .filter(Boolean)
                .join(' \u00b7 ')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {match.category === 'enrichment' && (
            <Select
              value={match.action}
              onValueChange={(val: string) => onActionChange(index, val as TBulkPolicyAction)} // AS_JUSTIFIED: Shadcn Select values are TBulkPolicyAction literals
            >
              <SelectTrigger className="h-8 w-[140px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="enrich">Enrich Existing</SelectItem>
                <SelectItem value="create">Create New</SelectItem>
                <SelectItem value="skip">Skip</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Per-policy insurance type selector */}
      {match.action !== 'skip' && match.category === 'new' && (
        <div className="flex items-center gap-2 pl-9">
          <span className="text-xs text-muted-foreground whitespace-nowrap">Type:</span>
          <Select
            value={match.assignedTypeId ?? ''}
            onValueChange={(val: string) => onTypeChange(index, val)}
          >
            <SelectTrigger
              className={cn('h-7 w-[200px] text-xs', showTypeWarning && 'border-destructive')}
            >
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {orgTypes.map(t => (
                <SelectItem key={t._id} value={t._id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {assignedType && <span className="text-xs text-muted-foreground">AI detected</span>}
          {showTypeWarning && <span className="text-xs text-destructive">Required</span>}
        </div>
      )}

      {showDiff && match.enrichmentDiff && match.enrichmentDiff.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setDiffExpanded(!diffExpanded)}
            className="text-xs text-amber-700 dark:text-amber-400 hover:underline font-medium"
          >
            {diffExpanded
              ? 'Hide changes'
              : `Show ${match.enrichmentDiff.filter(d => d.type !== 'match').length} changes`}
          </button>
          {diffExpanded && <EnrichmentDiffView diffs={match.enrichmentDiff} />}
        </>
      )}
    </div>
  );
}
